import { useHistory } from '@shared/core';
import { useEffect, useState, useRef, useMemo } from 'react';
import type { MutableRefObject } from 'react';
import type { PagesRefs } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.types';
import { usePageRefFromUrl } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/usePageRefFromUrl';
import { usePageInHistory } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/usePageInHistory';
import { useAccommodationsEditorLeadStateQuery } from '../../../../../../admin/routes/WebsiteEditor/customComponents/Accommodations/Accommodations.graphql.generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { isInIframe } from '@shared/utils/isInIframe';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { isScrollAnchoringSupported, SCROLL_AWAY_STOP_PX } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/scrollAnchoring';

type ScrollToPageWhenUrlChangesProps = Readonly<{
  pagesRef: ReadonlyArray<PagesRefs>;
  container: HTMLDivElement | null;
}>;

type UseScrollToPageResult = Readonly<{
  hasScrolled: boolean;
  currentPage: PagesRefs | null;
  /**
   * Whether `currentPage` is the first section — i.e. its landing means "the document top", since
   * readDestinationY returns 0 for it. Exposed so the pinning carve-outs here and in
   * useKeepPageScrollPosition share ONE computation with the landing rather than each re-deriving
   * it: if they ever disagreed, a landing of 0 could coincide with pinning enabled, which is
   * precisely the combination that drags the viewport off the top. PRNT-4799.
   */
  isCurrentPageFirst: boolean;
}>;

// Maximum retry attempts for layout completion (at 60fps = ~1 second)
const LAYOUT_RETRY_MAX_ATTEMPTS = 60;

// Corrections below this are sub-pixel noise — never worth a scrollTop write.
const POSITION_EPSILON_PX = 1;

// Element-tracking nav animation: duration scales with distance, clamped to feel like the
// native smooth scroll it replaces.
const NAV_SCROLL_MS_PER_PX = 0.35;
const NAV_SCROLL_MIN_DURATION_MS = 350;
const NAV_SCROLL_MAX_DURATION_MS = 900;

// Genuine guest inputs that must take over from any programmatic scrolling. pointerdown covers
// scrollbar drags and clicks, which fire none of the other three; it cannot self-cancel the nav
// that started the animation, because that tap's pointerdown fired before the listeners attach.
const GUEST_INPUT_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

// Offset of `el` within the scroller = its top relative to the CONTAINER's top, plus scrollTop.
// Invariant under scrolling, so any drift in it is purely layout movement.
//
// Container-relative, not viewport-relative: `scrollTop` is measured from the container's own top,
// and with a website banner active GuestSiteBody pushes that 68px (mobile) / 77px (desktop) below the
// viewport. A viewport-relative reading overstates every offset by that gap, which cancels in
// differences like `contentShift` but corrupts anything compared against `scrollTop` directly — the
// nav/landing destinations below, and the first-page reached test in the keeper. It also makes the
// value immune to the container itself moving, e.g. a banner collapsing. PRNT-4799.
const documentYOf = (el: Element, container: HTMLDivElement): number => el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;

const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// Composition rule shared with useKeepPageScrollPosition: whatever another mechanism (or the
// guest's own scrolling) already moved shows up in scrollChange and is not applied twice, and the
// clamp keeps accumulated user scroll from inflating the correction into a yank.
const clampedCorrection = (contentShift: number, scrollChange: number): number => {
  const raw = contentShift - scrollChange;
  return contentShift >= 0 ? Math.min(Math.max(raw, 0), contentShift) : Math.max(Math.min(raw, 0), contentShift);
};

// Document-Y of a nav/landing destination: the first page always means "the very top".
const readDestinationY = (container: HTMLDivElement, pageRef: PagesRefs, isFirstPage: boolean): number | null => {
  if (isFirstPage) return 0;
  return pageRef.ref ? documentYOf(pageRef.ref, container) : null;
};

type PositionKeeper = Readonly<{
  stop: () => void;
  /**
   * Re-baseline against the current position. Required after any programmatic scroll that ran
   * outside the keeper (a nav glide): the keeper is armed when `currentPage` updates, which
   * happens as the glide STARTS, so its baseline would otherwise describe the pre-glide
   * position. A later resize then reads the whole glide distance as `scrollChange` — dropping
   * the correction on a downward nav, and tripping the scroll-away stop on an upward one.
   */
  resync: () => void;
}>;

/**
 * Safari position keeper (see the effect that starts it for the full rationale): pins `pinned`
 * by its document-Y against height changes in its sibling sections. RO callbacks run after
 * layout, before paint, in the frame that resized — a displaced frame never paints.
 */
const startPositionKeeper = (container: HTMLDivElement, pinned: HTMLDivElement, isNavAnimating: () => boolean, isFirstPagePin: boolean): PositionKeeper | undefined => {
  const siblings = pinned.parentElement?.children;
  if (!siblings) return undefined;
  let baselineDocY = documentYOf(pinned, container);
  let lastScrollTop = container.scrollTop;
  // eslint-disable-next-line compat/compat
  const observer = new ResizeObserver(() => {
    // The pin can detach if its section remounts — a detached rect reads all zeros and would
    // produce one garbage correction.
    if (!pinned.isConnected) {
      stopObservers();
      return;
    }
    const scrollNow = container.scrollTop;
    // First-page pin: its landing was the document TOP, not the section's position (readDestinationY
    // returns 0 for it), so while the guest is above the pin — reading the standalone website header
    // — compensating growth would scroll them down past that header. Once they are at or past the
    // pin, holding it is exactly what keeps the section they are reading still.
    //
    // Recomputed every callback and never remembered. Nothing else can re-derive it: `currentPage`
    // is set by goToPage alone, so natural scrolling — in either direction, across the pin or back
    // into the header — never re-arms the keeper, calls resync, or re-runs the effect. Any stored
    // verdict therefore outlives the position it described: remembered `true` lets a guest who has
    // wandered back into the header be pushed down by the next resize, and remembered `false` denies
    // compensation to one who has since crossed. The upward-stop below is no substitute; it measures
    // movement between resize callbacks, so a short or incremental return never trips it.
    //
    // Judged against `baselineDocY`, measured BEFORE this resize, never against the pin's fresh rect:
    // scrolling cannot change document-Y, so this pair says where the guest stood when the growth
    // landed. The displaced rect cannot tell the cases apart — growth larger than the distance a
    // guest just crossed the pin puts its top back below the viewport top, reading identically to a
    // guest who never left the header. PRNT-4799.
    // Two conditions, because a header that has not rendered yet leaves the pin AT the document top:
    // `baselineDocY` is then ~0, every scroll offset satisfies the second test, and a guest sitting at
    // 0 would be scrolled down by exactly the height the header finally takes (measured: a 759px
    // jump onto the welcome section). Being at the top is itself the first page's landing, so nothing
    // may push off it; nothing above the pin can want compensating there either.
    // The 1px epsilon is only a meaningful reached-test once real content stands above the pin.
    // Pre-header the pin sits at the document top (`baselineDocY` ~0), so any nudge would clear both
    // clauses and the header's arrival would be applied in full against a 5px scroll — and a
    // partially-rendered header (baseline of a few px) has the same hole. So while the content above
    // the pin is smaller than SCROLL_AWAY_STOP_PX — the file's existing bar for "a genuine guest
    // scroll, not noise" — reaching the pin demands a scroll past that bar instead of past the
    // epsilon. A guest who went deep BEFORE the header loaded stays compensated; a guest who merely
    // nudged stays at the top, watching the header land. PRNT-4799.
    const reachBarPx = baselineDocY > SCROLL_AWAY_STOP_PX ? POSITION_EPSILON_PX : SCROLL_AWAY_STOP_PX;
    const hasReachedPin = !isFirstPagePin || (scrollNow > reachBarPx && scrollNow >= baselineDocY - POSITION_EPSILON_PX);
    if (!hasReachedPin) {
      baselineDocY = documentYOf(pinned, container);
      lastScrollTop = scrollNow;
      observeAboveThePin();
      return;
    }
    if (scrollNow < lastScrollTop - SCROLL_AWAY_STOP_PX && !isNavAnimating()) {
      stopObservers();
      return;
    }
    const contentShift = documentYOf(pinned, container) - baselineDocY;
    const correction = clampedCorrection(contentShift, scrollNow - lastScrollTop);
    if (Math.abs(correction) > POSITION_EPSILON_PX) {
      container.scrollTop += correction;
    }
    // Re-measure rather than assume: near the scroller's edges the write clamps, and an
    // assumed value would poison every later delta.
    baselineDocY = documentYOf(pinned, container);
    lastScrollTop = container.scrollTop;
    // Children arrive over time as sections render, so keep coverage complete as the tree fills in.
    observeAboveThePin();
  });
  // Observe every sibling in the section row: the header block and non-deferred sections move
  // the pin exactly the same way; resizes below the pin net a zero delta and no-op.
  //
  // For a first-page pin, the scroller's CHILDREN as well. Under Brannan the sections are nested a
  // dozen levels down, so the pin's sibling row is effectively just the pin — and the standalone
  // website header growing MOVES the pin without resizing it, so it produced no callback at all.
  // `baselineDocY` then stayed at its arm-time value (~0, since the keeper arms before the header has
  // a height) until some unrelated section resized, and that callback measured the header's whole
  // displacement as fresh content shift: a guest who had nudged the page a few pixels was pushed down
  // onto the first section. The container's children wrap all the content, so any growth anywhere
  // inside changes one of their heights whatever the depth, which keeps the baseline current while
  // the guest is above the pin (the branch above just re-baselines). Observing a node twice is a
  // no-op. PRNT-4799.
  const observeAboveThePin = () => {
    if (!isFirstPagePin) return;
    for (const el of Array.from(container.children)) observer.observe(el);
  };
  for (const el of Array.from(siblings)) observer.observe(el);
  observeAboveThePin();
  // Insertion is what the ResizeObserver cannot see: a lazy section mounting as a new container
  // child moves its siblings without resizing them, so no callback runs and the newcomer stays
  // unobserved until something else happens to resize — its own later image growth missed. Let
  // childList mutations drive coverage instead; observing a node twice is a no-op.
  let childListObserver: MutationObserver | undefined;
  if (isFirstPagePin && typeof MutationObserver !== 'undefined') {
    childListObserver = new MutationObserver(observeAboveThePin);
    childListObserver.observe(container, { childList: true });
  }
  // Every stop must take both observers down together: observe() on a disconnected ResizeObserver
  // REACTIVATES it, so a surviving childList observer would resurrect a keeper that detached or
  // stopped on scroll-away.
  const stopObservers = () => {
    observer.disconnect();
    childListObserver?.disconnect();
  };
  return {
    stop: stopObservers,
    resync: () => {
      if (!pinned.isConnected) return;
      baselineDocY = documentYOf(pinned, container);
      lastScrollTop = container.scrollTop;
    },
  };
};

type NavAnimationHandles = Readonly<{
  /** Cancels the currently running animation, if any; the animation stores its own cancel here. */
  cancelRef: MutableRefObject<(() => void) | null>;
  /** True while the animation is driving scrollTop — read by the position keeper. */
  animatingRef: MutableRefObject<boolean>;
  /** Called whenever the animation ends (arrival or guest takeover) — see PositionKeeper.resync. */
  onScrollSettled: () => void;
}>;

/**
 * Element-tracking animation instead of native `behavior: 'smooth'`: the native animation aims
 * at a fixed pixel offset and cannot be told the destination moved — sections between here and
 * there are `content-visibility: auto` placeholders that render at their real heights as the
 * scroll passes them, so a position captured once goes stale mid-flight (measured 456px into
 * /story). The easing here interpolates toward the LIVE element position each frame, so height
 * changes during travel are absorbed into the motion and the animation ends exactly at the
 * destination, by construction. Cancels on genuine guest input (the tap that triggered the nav
 * fired before the listeners attach, so it cannot self-cancel) and honors reduced motion.
 */
const animateScrollToDestination = (container: HTMLDivElement, pageRef: PagesRefs, isFirstPage: boolean, handles: NavAnimationHandles): void => {
  const initialTarget = readDestinationY(container, pageRef, isFirstPage);
  if (initialTarget === null) return;
  handles.cancelRef.current?.();
  let cancelled = false;
  let rafId: number | null = null;
  const cancel = () => {
    cancelled = true;
    handles.animatingRef.current = false;
    if (rafId !== null) cancelAnimationFrame(rafId);
    GUEST_INPUT_EVENTS.forEach(e => container.removeEventListener(e, cancel));
    if (handles.cancelRef.current === cancel) handles.cancelRef.current = null;
    // Arrival or guest takeover alike: the keeper's baseline predates this scroll either way.
    handles.onScrollSettled();
  };
  handles.cancelRef.current = cancel;
  handles.animatingRef.current = true;
  GUEST_INPUT_EVENTS.forEach(e => container.addEventListener(e, cancel, { passive: true }));

  const startTop = container.scrollTop;
  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
  const duration = prefersReducedMotion ? 0 : Math.min(NAV_SCROLL_MAX_DURATION_MS, Math.max(NAV_SCROLL_MIN_DURATION_MS, Math.abs(initialTarget - startTop) * NAV_SCROLL_MS_PER_PX));
  if (duration === 0) {
    container.scrollTo({ left: 0, top: initialTarget, behavior: 'auto' });
    cancel();
    return;
  }
  const startTime = performance.now();
  let lastEase = 0;
  const frame = () => {
    if (cancelled) return;
    const target = readDestinationY(container, pageRef, isFirstPage);
    if (target === null) return cancel();
    const t = Math.min(1, (performance.now() - startTime) / duration);
    if (t >= 1) {
      container.scrollTop = target;
      return cancel();
    }
    // Incremental, not `start + (target - start) * ease`: that formula is absolute, so a target
    // that moves mid-flight (a content-visibility section rendering at its real height) only has
    // `ease(t)` of its delta applied and the viewport jumps by the rest — and the absolute write
    // also overwrites Chrome's scroll-anchoring compensation. Covering a fraction of the LIVE
    // remaining distance instead absorbs target growth, anchoring adjustments and any other
    // mechanism's scroll writes without a jump, and still converges exactly: the fraction
    // reaches 1 as t does (with an explicit snap above for the final frame). PRNT-4799.
    const eased = easeInOutCubic(t);
    const step = Math.min(1, Math.max(0, (eased - lastEase) / (1 - lastEase)));
    lastEase = eased;
    container.scrollTop += (target - container.scrollTop) * step;
    rafId = requestAnimationFrame(frame);
  };
  rafId = requestAnimationFrame(frame);
};

/**
 * Handles scrolling to the correct page section when URL changes.
 *
 * For SSR pages: Scrolls immediately when elements are ready
 * For nossr=true (client-side only): Retries until layout is complete
 *
 * Retry mechanism:
 * - Checks if element has non-zero height (fully laid out)
 * - Retries on requestAnimationFrame until ready or timeout
 * - Prevents infinite loops with retry limit
 * - Handles zero-height pages (empty/hidden elements) with fallback
 *
 * Cleanup:
 * - Cancels animation frames before early returns to prevent stale closures
 * - Cancels on unmount/navigation to prevent memory leaks
 */
export const useScrollToPageWhenUrlChanges = ({ pagesRef, container }: ScrollToPageWhenUrlChangesProps): UseScrollToPageResult => {
  const history = useHistory();
  // Admin preview (joy's own preview iframe) — NOT bare isInIframe(), which is also true for Carbon
  // embeds and masked/custom domains where this is a real guest visit. Mirrors MobileTopBarNav's
  // detection, including the `ctx` fallback: the EventWorkspace dashboard builds its iframe src with
  // HTML-entity `&amp;` separators, which mangles the `preview` key to `amp;preview`.
  const { getValueString } = useQueryParamHelper();
  const isAdminPreview = isInIframe() && (getValueString('preview') === 'true' || getValueString('ctx') === 'adminGuestSitePreview');
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasNormalizedUrl, setHasNormalizedUrl] = useState(false);
  const [currentPage, setCurrentPage] = useState<PagesRefs | null>(null);
  const previousPathnameRef = useRef<string | undefined>(undefined);
  const animationFrameIdRef = useRef<number | null>(null);
  // Cancels the previous nav scroll's element-tracking animation — see scrollToPage.
  const navAnimationCancelRef = useRef<(() => void) | null>(null);
  // True while our element-tracking animation is driving scrollTop — the keeper below must not
  // read those frames as the guest scrolling away.
  const navAnimatingRef = useRef(false);
  // Re-baselines the live position keeper once a nav scroll settles — see PositionKeeper.resync.
  const keeperResyncRef = useRef<(() => void) | null>(null);
  // Page id of the last DELIBERATE navigation (initial landing or PUSH). The re-scroll below must
  // chase this, never the live URL: while preview content streams in, growth can push the target
  // out of view faster than corrections land, and usePageInScreenWhenScrolling then REPLACEs the
  // URL to whatever section got displaced into the viewport. Re-reading the URL at that moment
  // re-lands on the displaced section and cements the wrong page — traced in the designer as the
  // target flipping vip -> travel mid-load, intermittently, by exactly this race. PRNT-4799.
  const lastNavTargetPageIdRef = useRef<string | null>(null);

  // Unmount only: stop a mid-flight nav animation and drop its container listeners. Deliberately
  // NOT part of the main scroll effect's cleanup — that effect re-runs on every pagesRef/URL
  // change (sections stream in during a glide) and would kill the animation mid-flight.
  useEffect(() => () => navAnimationCancelRef.current?.(), []);
  // Preview only: a PUSH navigated to a page whose data isn't in `pagesRef` yet (e.g. a
  // just-created page whose section streams in after an `adminEventPageChange` refetch). We can't
  // scroll to it at navigation time, so remember the target and scroll once `pagesRef` includes it.
  const pendingScrollPathnameRef = useRef<string | null>(null);
  const pages = useMemo(() => pagesRef.map(pr => pr.page), [pagesRef]);
  const getPageRefFromUrl = usePageRefFromUrl(pages);
  const replacePageInBrowserHistory = usePageInHistory(pages);

  const { eventHandle } = useEventInfo();
  const { data: accommodationsLeadStateData } = useAccommodationsEditorLeadStateQuery({
    variables: { eventHandle: eventHandle! },
    batchMode: 'fast',
    skip: !eventHandle,
    // Only used client-side in useEffect for scroll behavior — no need during SSR.
    ssr: false,
  });
  const hasBookingLink = !!accommodationsLeadStateData?.eventByName?.hotelRoomBlocks?.bookingLink;
  const [isInstantScrollEnabled] = useIsFeatureEnabled({ eventFeatureFlagKey: 'useInstantScrollBehavior' });

  // Refs for values the ResizeObserver callback reads after mount.
  const pagesRefLatest = useRef(pagesRef);
  pagesRefLatest.current = pagesRef;
  const containerLatest = useRef(container);
  containerLatest.current = container;
  // getPageRefFromUrl captures history.location in its useCallback deps, so it
  // can become stale if the user navigates after mount.
  const getPageRefFromUrlLatest = useRef(getPageRefFromUrl);
  getPageRefFromUrlLatest.current = getPageRefFromUrl;

  // Post-hydration re-scroll: widgets wrapped in <NoSsr> or <WidgetTransition>
  // mount after hydration and their data can arrive at any time up to the 3s
  // fallback from `PageContentVisibilityProvider`. A single fixed delay (e.g.,
  // 400ms) is too fragile — if content settles afterwards, deep-linked routes
  // land at the wrong offset.
  //
  // Instead, observe the scroll container's size: every time it grows (because
  // a widget rendered its data or chrome faded in), re-scroll to the target
  // page's anchor. Stop observing when the user interacts (wheel/touch/key) so
  // we don't fight their intent, or after 5s — well past the provider's 3s fallback.
  //
  // SYNCHRONOUS AND INSTANT, never rAF-deferred or smooth: ResizeObserver callbacks run in the
  // rendering steps AFTER layout but BEFORE paint, so a scroll applied right here lands in the
  // same frame as the resize and is invisible — zero layout shift. The previous
  // requestAnimationFrame deferral pushed the scroll to the NEXT frame (the displaced frame had
  // already painted → counted as CLS), and `behavior: 'smooth'` then smeared the movement across
  // dozens of frames, each able to collide with in-flight widget growth. This is a background
  // position correction, not user-facing motion — nav-click scrolling below keeps its animation.
  // PRNT-4799.
  useEffect(() => {
    const container = containerLatest.current;
    if (!container || typeof ResizeObserver === 'undefined') return;

    // Safari fallback: where scroll anchoring exists (with deterministic, target-only candidacy) it
    // holds the deep-linked section inside the layout pass — re-scrolling here measures first, and
    // the measurement forces the layout in which the shift gets recorded. See scrollAnchoring.ts.
    // The initial goToPage scroll below is unaffected. PRNT-4799.
    //
    // The admin preview needs it too, despite Chrome reporting anchoring support. Traced on a
    // Simple Scroll event: the landing fires once at 3.6s against a 6,832px document that ends up
    // 24,784px, and the target then drifts from docY 3,418 to 13,881 with `scrollTop` never moving
    // again — no anchoring adjustment, no compensation of any kind. Anchoring cannot work there:
    // candidacy is confined to the target's sentinel (which the stale landing leaves far below the
    // fold), section deferral is off in preview by design, `nossr` makes the growth enormous, and
    // the editor's scale churn zeroes the container's layout box repeatedly mid-load.
    //
    // Gated on the admin preview specifically, never on iframe presence: in a Carbon embed or a
    // masked domain this is a real guest, and bypassing anchoring there would leave an unbounded
    // observer able to yank them back to the deep-link target (clicks and scrollbar drags aren't
    // among the interaction listeners below, so they wouldn't even stop it).
    if (isScrollAnchoringSupported() && !isAdminPreview) return;

    let userInteracted = false;
    let lastScrollHeight = -1;

    const reScroll = () => {
      const currentPagesRef = pagesRefLatest.current;
      const currentContainer = containerLatest.current;
      if (!currentPagesRef.length || !currentContainer) return;

      // Admin preview: the pinned nav target, not the live URL — see lastNavTargetPageIdRef. Falls
      // back to the URL before any landing has recorded a target. Preview-scoped like the child
      // observation that makes this branch fire at all: on the guest paths the observer never fires
      // (the container's own 100vh box never changes), so pinning would change nothing there — but
      // scoping makes that a guarantee instead of a consequence, and keeps every guest byte-path
      // reading the URL exactly as before.
      const pinnedId = isAdminPreview ? lastNavTargetPageIdRef.current : null;
      const pageRef = pinnedId ? currentPagesRef.find(pr => pr.page.id === pinnedId) : getPageRefFromUrlLatest.current(currentPagesRef);
      if (!pageRef?.ref) return;

      if (pageRef.ref.getBoundingClientRect().height === 0) return;

      const isFirstPage = pageRef.page.id === currentPagesRef[0].page.id;
      // The same destination the landing and nav paths compute, via readDestinationY/documentYOf, so
      // all three agree. Computing `rect.top + scrollTop` here was viewport-relative: with a website
      // banner offsetting the scroller it overshot by the banner's height, and since a
      // ResizeObserver delivers one notification right after `observe`, this callback overwrote the
      // corrected landing immediately — leaving the target above the container's top edge, behind
      // the banner. PRNT-4799.
      const y = readDestinationY(currentContainer, pageRef, isFirstPage);
      if (y === null) return;
      currentContainer.scrollTo({ left: 0, top: y, behavior: 'auto' });
    };

    const markInteraction = () => {
      userInteracted = true;
    };
    container.addEventListener('wheel', markInteraction, { passive: true });
    container.addEventListener('touchstart', markInteraction, { passive: true });
    container.addEventListener('keydown', markInteraction);
    // pointerdown: scrollbar drags dispatch it on the scrolling element but fire none of the three
    // above, so a designer dragging the preview's scrollbar was fought by the pinned re-land below.
    // Also covers clicks into the preview (inline editing) — engagement with a spot the re-scroll
    // must not yank away from. PRNT-4799.
    container.addEventListener('pointerdown', markInteraction, { passive: true });

    // ResizeObserver is unsupported on iOS Safari 11.0-11.2; we've already
    // guarded on `typeof ResizeObserver === 'undefined'` above, so older
    // browsers skip the re-scroll entirely (degrades to the initial scroll).
    // eslint-disable-next-line compat/compat
    const observer = new ResizeObserver(() => {
      if (userInteracted) {
        observer.disconnect();
        return;
      }
      // No layout box: the editor toggles the preview iframe's `display` while applying scale, and
      // a hidden container reports every metric as 0. Reading it would re-scroll to garbage and
      // poison `lastScrollHeight`; the growth is still coming, so just wait it out. Preview-only,
      // like the toggle that causes it.
      if (isAdminPreview && container.clientHeight === 0) return;
      const h = container.scrollHeight;
      if (h === lastScrollHeight) return;
      lastScrollHeight = h;
      reScroll();
      // Children arrive over time (nossr renders section by section); observing a node twice is a
      // no-op, so this keeps coverage complete as the tree fills in.
      if (isAdminPreview) {
        for (const el of Array.from(container.children)) observer.observe(el);
      }
    });
    // In the preview: the scroller's CHILDREN, not the scroller. The container is `height: 100vh`,
    // so its own box never changes when content grows and observing it caught nothing — traced as
    // enabled but silent through 10 growth events totalling +18,000px. Its children wrap all the
    // content, so any growth anywhere inside changes one of their heights, whatever the nesting
    // depth (Brannan buries sections 12 levels down). PRNT-4799.
    //
    // The Safari guest path keeps observing the container itself, unchanged — and by that same
    // `100vh` reasoning it never fires there either, nor does the position keeper below (which
    // observes the pin's siblings: just the pin, under Brannan's 12-level nesting). What does still
    // cover Safari guests is useKeepPageScrollPosition's MutationObserver on `{childList, subtree}`,
    // so growth from mounting nodes IS compensated — but resize-only growth is not: a late image, a
    // font swap, or a class-driven expansion (that hook calls out the accommodations More/Less
    // toggle explicitly). Narrow, and SSR plus section deferral shrink it further; both are off in
    // preview, which is why only preview broke outright. Switching this would activate a re-scroll
    // on real guest deep links: plausibly right, but untested on Safari and CLS-sensitive, so it
    // wants its own change — arguably repairing the keeper's sibling assumption instead, since that
    // compensates rather than re-lands. Not tracked in a ticket yet.
    if (isAdminPreview) {
      for (const el of Array.from(container.children)) observer.observe(el);
    } else {
      observer.observe(container);
    }

    // Stop observing well after the provider's 3s reveal-all fallback timer. Not applied in the
    // preview: growth there runs as long as the client-side render takes (traced settling at 7s,
    // and a slower machine or event has no bound), and a wall-clock cutoff would fail exactly the
    // loads that need this most. The observer goes quiet on its own once heights stop changing, and
    // genuine guest input disconnects it above.
    const giveUpTimer = isAdminPreview ? null : setTimeout(() => observer.disconnect(), 5000);

    return () => {
      observer.disconnect();
      if (giveUpTimer) clearTimeout(giveUpTimer);
      container.removeEventListener('wheel', markInteraction);
      container.removeEventListener('touchstart', markInteraction);
      container.removeEventListener('keydown', markInteraction);
      container.removeEventListener('pointerdown', markInteraction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, isAdminPreview]); // re-attach when the scroll container ref populates (null -> DOM node on first render)

  // Unified Safari position keeper — ONE ResizeObserver for the whole page session.
  //
  // Chrome's native scroll anchoring holds the section the guest was programmatically taken to
  // (deep-link landing or nav) while surrounding sections reach their real heights. Safari has
  // no scroll anchoring, so this replicates exactly that: pin `currentPage` (already updated by
  // goToPage on landing AND on every nav) by its document-Y. RO callbacks run after layout,
  // before paint, in the same frame that resized — a displaced frame never paints.
  //
  // Composition rule (same as useKeepPageScrollPosition): correction = contentShift −
  // scrollChange, clamped to the observed shift. Whatever another mechanism or the guest's own
  // scrolling already moved is not applied twice, and accumulated user scroll can never inflate
  // the correction into a yank. Stops when the guest genuinely scrolls upward (releases sit
  // between the viewport and the pin up there, where pinning would yank them); re-arms on the
  // next landing/nav because the effect re-runs per `currentPage`. PRNT-4799.
  // First-page handling, same as useKeepPageScrollPosition's: readDestinationY returns 0 for the
  // first page, so its landing means "the document top", not that section's current position.
  // Pinning the section instead lets growth in the header ABOVE it be compensated by scrolling
  // down, off the top of the page. This keeper pins document-Y exactly like that hook, so it needs
  // the same treatment or it reproduces the drag on its own — on Aloha in particular, where
  // sections ARE the scroller's direct children and every one of them is observed. The keeper
  // therefore holds off only while the guest is above the pin, rather than being disarmed for the
  // session: `currentPage` is set by goToPage alone, so after a root landing it stays the first
  // section however far the guest scrolls. Compared by page id, never by ref: a section remount
  // swaps the node and keeps the id. PRNT-4799.
  const isCurrentPageFirst = !!currentPage && !!pagesRef[0] && currentPage.page.id === pagesRef[0].page.id;

  useEffect(() => {
    const pinned = currentPage?.ref;
    if (!container || !pinned || isScrollAnchoringSupported() || typeof ResizeObserver === 'undefined') return;
    const keeper = startPositionKeeper(container, pinned, () => navAnimatingRef.current, isCurrentPageFirst);
    if (!keeper) return;
    keeperResyncRef.current = keeper.resync;
    return () => {
      keeperResyncRef.current = null;
      keeper.stop();
    };
  }, [container, currentPage, isCurrentPageFirst]);

  useEffect(() => {
    if (!hasNormalizedUrl && pagesRef?.length) {
      const pageRef = getPageRefFromUrl(pagesRef);

      // if the current slug is not valid, let's replace the url by the firsts available event page url
      if (!pageRef) {
        if (history.location.pathname.includes('accommodations') && hasBookingLink) {
          // intercept in the case of aloha to allow guest site to render bookinglink redirect component
          return;
        }
        replacePageInBrowserHistory(pagesRef[0].page);
      }

      setHasNormalizedUrl(true);
    }
  }, [pagesRef, hasNormalizedUrl, getPageRefFromUrl, replacePageInBrowserHistory, history.location.pathname, hasBookingLink]);

  useEffect(() => {
    // Cancel any previous retry chains FIRST, before any early returns
    if (animationFrameIdRef.current !== null) {
      cancelAnimationFrame(animationFrameIdRef.current);
      animationFrameIdRef.current = null;
    }

    if (!pagesRef?.length || !container) {
      return;
    }

    const scrollToPage = (pageRef: PagesRefs, isSmooth: boolean) => {
      const isFirstPage = pageRef.page.id === pagesRef[0].page.id;
      if (!isSmooth || isInstantScrollEnabled) {
        navAnimationCancelRef.current?.();
        const targetY = readDestinationY(container, pageRef, isFirstPage);
        if (targetY !== null) {
          container.scrollTo({ left: 0, top: targetY, behavior: 'auto' });
        }
        return;
      }
      animateScrollToDestination(container, pageRef, isFirstPage, {
        cancelRef: navAnimationCancelRef,
        animatingRef: navAnimatingRef,
        onScrollSettled: () => keeperResyncRef.current?.(),
      });
    };

    const goToPage = (pageRef: PagesRefs, hasScrolled: boolean) => {
      if (pageRef?.ref) {
        const attemptScroll = (retryCount = 0) => {
          // Admin preview only: wait for the container to have a layout box before measuring the
          // landing. The editor keeps the preview `display: none` until its resize spring settles,
          // and a hidden container reports every metric as 0 — so the retry budget below burned out
          // against that churn and the fallback landed on garbage. Waiting does not consume the
          // budget: the churn's duration belongs to the editor, not to this layout, and it is
          // bounded by the editor's own reveal. Deliberately NOT gated on document.readyState — one
          // stalled media request would defer every landing, editor page-switches included, with no
          // timeout. Guest paths are untouched: no editor, no churn.
          if (isAdminPreview && container.clientHeight === 0) {
            animationFrameIdRef.current = requestAnimationFrame(() => attemptScroll(retryCount));
            return;
          }
          const rect = pageRef.ref?.getBoundingClientRect();

          if (rect && rect.height > 0) {
            // Element has been laid out with non-zero height, scroll now
            scrollToPage(pageRef, hasScrolled);
            setCurrentPage(pageRef);
            setHasScrolled(true);
            animationFrameIdRef.current = null;
          } else if (retryCount < LAYOUT_RETRY_MAX_ATTEMPTS && pageRef.ref) {
            // Layout not complete yet, retry on next frame
            animationFrameIdRef.current = requestAnimationFrame(() => attemptScroll(retryCount + 1));
          } else {
            // Fallback: either hit retry limit or ref became null
            // Attempt scroll anyway (handles zero-height pages, hidden elements, etc.)
            scrollToPage(pageRef, hasScrolled);
            setCurrentPage(pageRef);
            setHasScrolled(true);
            animationFrameIdRef.current = null;
          }
        };

        // Call directly for first attempt - SSR pages scroll immediately,
        // nossr pages will schedule RAF retries as needed
        attemptScroll(0);
      }
    };

    // Go to the Page when Page loads the first time or when pathname changes via navigation
    const currentPathname = history.location.pathname;
    const isPathnameChanged = previousPathnameRef.current !== currentPathname;

    const isPushNavigationWithPathChange = history.action === 'PUSH' && isPathnameChanged;
    // Re-run for a still-pending target (its data has since arrived in `pagesRef`), even though the
    // pathname didn't change this time — see `pendingScrollPathnameRef`.
    const hasPendingScroll = pendingScrollPathnameRef.current === currentPathname;
    const shouldScroll = !hasScrolled || isPushNavigationWithPathChange || hasPendingScroll;

    const isIframeReplaceTracking = hasScrolled && isInIframe() && history.action === 'REPLACE';

    if (shouldScroll && !isIframeReplaceTracking) {
      const pageRef = getPageRefFromUrl(pagesRef);

      if (pageRef) {
        lastNavTargetPageIdRef.current = pageRef.page.id;
        goToPage(pageRef, hasScrolled);
        pendingScrollPathnameRef.current = null;
      } else if (isPushNavigationWithPathChange && isInIframe()) {
        // Target page not in `pagesRef` yet — remember it so the next `pagesRef` update scrolls to
        // it. Preview only, so guest-site navigation behavior is unchanged.
        pendingScrollPathnameRef.current = currentPathname;
      }
    }

    previousPathnameRef.current = currentPathname;

    // Cleanup animation frame on unmount or dependency change
    return () => {
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, [container, getPageRefFromUrl, hasScrolled, history.action, history.location.pathname, isAdminPreview, isInstantScrollEnabled, pagesRef, setCurrentPage, setHasScrolled]);

  return { hasScrolled, currentPage, isCurrentPageFirst };
};
